import { FunctionComponent, useCallback, useState, useEffect, useMemo, useRef } from 'react';
import styles from "./ReferralTracking.module.css";
import cn from 'classnames';
import {Image} from 'react-bootstrap';
import SidebarLayout from '../../../component/common/Layout/SidebarLayout/SidebarLayout';
import Calender from '../../../component/common/Calender/Calender';
import CustomButton from '../../../component/common/CustomButton/CustomButton';
import SearchInput from '../../../component/common/SearchInput/SearchInput';
import TextHeader from '../../../component/common/TextHeader/TextHeader';
import { IDifferenceType } from '../../../component/common/OverviewCard/OverviewCard';
import ProAccountsCard from '../../../component/common/ProAccountsCard/ProAccountsCard';
import { ASSESTS } from '../../../constant/assest';
import ReferralTrackingTable from './component/ReferralTrackingTable/ReferralTrackingTable';
import TopInvitersSlider from '../../../component/UI/TopInvitersSlider/TopInvitersSlider';
import RewardCalculationCard from '../../../component/UI/RewardCalculationCard/RewardCalculationCard';
import { endOfYear, startOfYear } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import { fetchReferralTrackingAnalytics, fetchReferralTrackingData, downloadReferralTrackerList } from '../../../redux/slices/referralTracker/actions';
import { useDebounce } from '../../../hooks/useDebounce';
import ErrorService from '../../../services/errorService';
import {toast} from 'react-toastify';
import ProAccountsCardSkeleton from '../../../component/common/ProAccountsCard/ProAccountsCardSkeleton';

const ReferralTracking: FunctionComponent = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { analytics, analyticsLoading, data, dataLoading } = useSelector((state: RootState) => state.referralTracker);

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([startOfYear(new Date()), endOfYear(new Date())]);
    const [activeFilter, setActiveFilter] = useState<string | null>('currentYear');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(5);
    const [isDownloading, setIsDownloading] = useState(false);
    const formatDate = useCallback((date: Date) => {        
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        return `${year}-${month}-${day}`;
    }, []);

    const formattedStartDate = useMemo(() => dateRange[0] ? formatDate(dateRange[0]) : undefined, [dateRange, formatDate]);
    const formattedEndDate = useMemo(() => dateRange[1] ? formatDate(dateRange[1]) : undefined, [dateRange, formatDate]);

    useEffect(() => {
        dispatch(fetchReferralTrackingAnalytics());
    }, [dispatch]);

    const prevSearchTerm = useRef(debouncedSearchTerm);
    const prevFormattedStartDate = useRef(formattedStartDate); 
    const prevFormattedEndDate = useRef(formattedEndDate); 

    useEffect(() => {
        if (formattedStartDate && formattedEndDate) {
            const searchChanged = prevSearchTerm.current !== debouncedSearchTerm;
            const startDateChanged = prevFormattedStartDate.current !== formattedStartDate; 
            const endDateChanged = prevFormattedEndDate.current !== formattedEndDate;     

            if ((searchChanged || startDateChanged || endDateChanged) && page !== 1) {
                setPage(1);
                return;
            }

            const params: any = {
                limit,
                search: debouncedSearchTerm || undefined,
                startDate: formattedStartDate, 
                endDate: formattedEndDate,     
            };
            if (!searchChanged && !startDateChanged && !endDateChanged) {
                params.page = page;
            }

            dispatch(fetchReferralTrackingData(params));

            // Update refs for the next render.
            prevSearchTerm.current = debouncedSearchTerm;
            prevFormattedStartDate.current = formattedStartDate;
            prevFormattedEndDate.current = formattedEndDate;     
        }
    }, [dispatch, page, limit, debouncedSearchTerm, formattedStartDate, formattedEndDate]);
    

    const handleDateChange = useCallback((range: [Date | null, Date | null], filter: string | null) => {
        setDateRange(range);
        setActiveFilter(filter);
    }, []);

    const handleDownload = useCallback(async () => {
        const params: any = {
            search: debouncedSearchTerm || undefined,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
        };
        setIsDownloading(true);
        try {
            const blob = await dispatch(downloadReferralTrackerList(params)).unwrap();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'referrals.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error(ErrorService.getErrorMessage(error));
        } finally {
            setIsDownloading(false);
        }
    }, [dispatch, debouncedSearchTerm, formattedStartDate, formattedEndDate]);

    const overView = useMemo(() => analytics ? [
        { title: 'Total Invitations', value: analytics.totalInvites, differenceType: analytics.inviteGrowthPercent >= 0 ? IDifferenceType.INCREASE : IDifferenceType.DECREASE, differenceText: `${analytics.inviteGrowthPercent}% from last month` },
        { title: 'Successful Sign-Ups', value: analytics.totalSignups, differenceType: analytics.signUpsGrowthPercent >= 0 ? IDifferenceType.INCREASE : IDifferenceType.DECREASE, differenceText: `${analytics.signUpsGrowthPercent}% conversion rate` },
    ] : [], [analytics]);

    return (
        <SidebarLayout>
            <TextHeader
                title={'User Referral Tracker'}
                summary={"Monitor and manage your referral program performance"}
                className='mb-4'
            />
            <div className={styles.raferalTrackerPage}>
                <div className={cn(styles.raferalTrackerCol, styles.raferalTrackerDataCol)}>
                    <div className={cn(styles.proAccountsTopRow)}>
                        <div className={styles.proAccountsCardsRow}>
                            {analyticsLoading === 'pending' ? (                     
                                Array.from({length: 2}).map((_, index)=> (
                                    <ProAccountsCardSkeleton key={index} className={styles.proAccountsOverViewCard} />
                                ))
                            ) : (
                                overView.map((item, index) => (
                                    <ProAccountsCard
                                        key={`${item.title}-${index}`}
                                        className={styles.proAccountsOverViewCard}
                                        title={item.title}
                                        value={item.value}
                                        differenceType={item.differenceType}
                                        differenceText={item.differenceText}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                    <div className={cn(styles.dasboardBottomRow)}>
                        <TextHeader
                            title={'Accounts that Invited Friends'}
                            summary={'Click on any row to view detailed information'}
                            className='mb-2'
                        />
                        <div className={styles.tableFilter}>
                            <SearchInput placeholder='Search users' className={styles.proAccountsSearch} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            <div className={cn(styles.tableFilter, styles.tableFilterCols)}>
                                <div className={cn(styles.selectFilter, styles.calenderFilter)}>
                                    <Calender onDateChange={handleDateChange} dateRange={dateRange} activeFilter={activeFilter} />
                                </div>
                            </div>
                            <div className={styles.downloadBtnWrap}>
                                <CustomButton className={styles.downloadBtn} onClick={handleDownload} disabled={isDownloading}>
                                    {isDownloading ? 'Downloading...' : 'Download'} <Image src={ASSESTS.DOWNLOADBTN} alt={'Download'} />
                                </CustomButton>
                            </div>
                        </div>
                        <ReferralTrackingTable
                            data={data?.data || []}
                            isLoading={dataLoading === 'pending'}
                            page={data?.page || 1}
                            limit={data?.limit || 5}
                            total={data?.total || 0}
                            totalPages={data?.totalPages || 0}
                            onPageChange={setPage}
                            onLimitChange={setLimit}
                        />
                    </div>
                </div>
                <div className={cn(styles.raferalTrackerCol, styles.raferalTrackerSidebarCol)}>
                    <TopInvitersSlider className={styles.referalSlider} />
                    <RewardCalculationCard
                        title={'Reward Calculation'}
                        summary={'(On at least one Successful Referral)'}
                        description={[
                            '• Monthly Giveaways to top 3 Anglers',
                            '• Rewards processed monthly'
                        ]}
                        className={styles.referalCondition}
                    />
                </div>
            </div>
        </SidebarLayout>
    );
};

export default ReferralTracking;